import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Modal,
  Form,
  Select,
  DatePicker,
  Progress,
  Avatar,
  Tooltip,
  Badge,
} from "antd";
import {
  FaProjectDiagram,
  FaSearch,
  FaEye,
  FaCheckCircle,
  FaPauseCircle,
  FaExclamationTriangle,
  FaEdit,
  FaPlus,
  FaPlayCircle,
  FaClock,
  FaFilter,
  FaCalendarAlt,
  FaUsers,
  FaFlag,
  FaLightbulb,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import Api from "../api/Api";
import dayjs from "dayjs";
import "./Projeto.css";

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDate = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return str;
  }
};

const getStatusInfo = (id_status) => {
  const map = {
    1: { label: "ABERTO", color: "blue", icon: <FaPlayCircle /> },
    2: { label: "FECHADO", color: "green", icon: <FaCheckCircle /> },
    3: { label: "SUSPENSO", color: "orange", icon: <FaPauseCircle /> },
    4: { label: "EM ANÁLISE", color: "purple", icon: <FaClock /> },
  };
  return map[id_status] || { label: "DESCONHECIDO", color: "default", icon: null };
};

const getCriticidadeInfo = (id_criticidade) => {
  const map = {
    1: { label: "BAIXA", color: "success", icon: <FaArrowDown />, bgColor: "#f6ffed", textColor: "#52c41a" },
    2: { label: "MÉDIA", color: "warning", icon: <FaMinus />, bgColor: "#fffbe6", textColor: "#faad14" },
    3: { label: "ALTA", color: "error", icon: <FaArrowUp />, bgColor: "#fff1f0", textColor: "#ff4d4f" },
  };
  return map[id_criticidade] || { label: "—", color: "default", icon: null, bgColor: "#f5f5f5", textColor: "#8c8c8c" };
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE ADICIONAR/EDITAR PROJETO
───────────────────────────────────────────────────────────────────────────── */
const ProjetoFormModal = ({
  open,
  onClose,
  onSuccess,
  editingProjeto,
  userStored,
  criticidadeList,
  iniciativaList,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (open) {
      loadUsuarios();
      if (editingProjeto) {
        form.setFieldsValue({
          titulo: editingProjeto.titulo,
          descricao: editingProjeto.descricao,
          inicio: editingProjeto.inicio ? dayjs(editingProjeto.inicio) : null,
          fim: editingProjeto.fim ? dayjs(editingProjeto.fim) : null,
          id_responsaveis: editingProjeto.responsaveis || [],
          id_criticidade: editingProjeto.id_criticidade,
          id_iniciativa: editingProjeto.id_iniciativa,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, editingProjeto, form]);

  const loadUsuarios = async () => {
    try {
      setLoadingUsers(true);
      const payload = { id_direcao: userStored.direcao };
      const response = await Api.ListUserDirecao(payload);
      setUsuarios(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar usuários.");
      setUsuarios([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        id_user: userStored.id,
        titulo: values.titulo,
        descricao: values.descricao,
        inicio: values.inicio ? values.inicio.format("YYYY-MM-DD") : null,
        fim: values.fim ? values.fim.format("YYYY-MM-DD") : null,
        id_direcao: userStored.direcao,
        id_responsaveis: values.id_responsaveis || [],
        id_criticidade: values.id_criticidade,
        id_iniciativa: values.id_iniciativa,
      };

      if (editingProjeto) {
        await Api.AlterProjeto({
          ...payload,
          id: editingProjeto.id,
          id_status: editingProjeto.id_status,
        });
        toast.success("Projeto atualizado com sucesso!");
      } else {
        await Api.AddProjeto(payload);
        if (userStored.diretor === "N") {
          toast.success("O seu projecto precisa de aprovação do director!");
        } else {
          toast.success("Projeto criado com sucesso!");
        }
      }

      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      if (error.errorFields) {
        toast.error("Preencha todos os campos obrigatórios.");
      } else {
        toast.error(
          editingProjeto
            ? "Erro ao atualizar projeto."
            : "Erro ao criar projeto."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={700}
      title={
        <Space>
          <FaProjectDiagram style={{ color: "#1677ff", fontSize: 20 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>
            {editingProjeto ? "Editar Projeto" : "Novo Projeto"}
          </span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 6 }}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          style={{ borderRadius: 6, fontWeight: 500 }}
        >
          {editingProjeto ? "Atualizar" : "Criar Projeto"}
        </Button>,
      ]}
      bodyStyle={{ padding: "24px 24px 0" }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Título</span>}
          name="titulo"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <Input placeholder="Ex: Desenvolvimento do Portal Interno" size="large" />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Descrição</span>}
          name="descricao"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <TextArea rows={4} placeholder="Detalhes do projeto..." />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Iniciativa</span>}
              name="id_iniciativa"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <Select
                size="large"
                placeholder="Selecione a iniciativa"
                showSearch
                optionFilterProp="children"
              >
                {iniciativaList.map((iniciativa) => (
                  <Option key={iniciativa.id} value={iniciativa.id}>
                    <Space>
                      <FaLightbulb style={{ color: "#1677ff" }} />
                      {iniciativa.titulo}
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Criticidade</span>}
              name="id_criticidade"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <Select size="large" placeholder="Selecione a criticidade">
                {criticidadeList.map((crit) => {
                  const info = getCriticidadeInfo(crit.id);
                  return (
                    <Option key={crit.id} value={crit.id}>
                      <Space>
                        <span style={{ color: info.textColor }}>{info.icon}</span>
                        {crit.descricao}
                      </Space>
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Data de Início</span>}
              name="inicio"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Selecione"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Data de Fim</span>}
              name="fim"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Selecione"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Responsáveis</span>}
          name="id_responsaveis"
          rules={[{ required: true, message: "Selecione pelo menos um responsável" }]}
        >
          <Select
            mode="multiple"
            placeholder="Selecione os responsáveis"
            loading={loadingUsers}
            size="large"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {usuarios.map((user) => (
              <Option key={user.id} value={user.id}>
                {user.nome}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   PROJETO PAGE
───────────────────────────────────────────────────────────────────────────── */
const Projeto = () => {
  const [projetos, setProjetos] = useState([]);
  const [criticidadeList, setCriticidadeList] = useState([]);
  const [iniciativaList, setIniciativaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("TODOS");
  const [criticidadeFilter, setCriticidadeFilter] = useState("TODAS");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingProjeto, setEditingProjeto] = useState(null);

  const navigate = useNavigate();
  const userStored = JSON.parse(localStorage.getItem("user")) || {};

  useEffect(() => {
    document.title = "Gestão de Projetos | Dashboard";
  }, []);

  const loadCriticidade = useCallback(async () => {
    try {
      const response = await Api.ListCriticidade();
      setCriticidadeList(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar criticidades.");
      setCriticidadeList([]);
    }
  }, []);

  const loadIniciativa = useCallback(async () => {
    try {
      const response = await Api.ListIniciativa();
      setIniciativaList(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar iniciativas.");
      setIniciativaList([]);
    }
  }, []);

  const loadProjetos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Api.ListProjeto({ id_direcao: userStored.direcao });
      const list = Array.isArray(response) ? response : [];

      const projetosComResponsaveis = await Promise.all(
        list.map(async (projeto) => {
          try {
            const payload = { id: projeto.id };
            const responsaveis = await Api.ListResponsavelProjeto(payload);
            const idsResponsaveis = Array.isArray(responsaveis)
              ? responsaveis.map((r) => r.id)
              : [];
            return {
              ...projeto,
              responsaveisNomes: Array.isArray(responsaveis) ? responsaveis : [],
              responsaveis: idsResponsaveis,
            };
          } catch {
            return { ...projeto, responsaveisNomes: [], responsaveis: [] };
          }
        })
      );

      setProjetos(projetosComResponsaveis);
    } catch {
      toast.error("Erro ao carregar projetos.");
      setProjetos([]);
    } finally {
      setLoading(false);
    }
  }, [userStored.direcao]);

  useEffect(() => {
    loadCriticidade();
    loadIniciativa();
    loadProjetos();
  }, [loadCriticidade, loadIniciativa, loadProjetos]);

  // Função que verifica se o usuário pode editar o projeto
  const canEditProject = (projeto) => {
    if (!projeto) return false;
    // Apenas diretores (diretor = "S") podem editar
    if (userStored.diretor !== "S") return false;
    // Projeto fechado NÃO pode ser editado
    if (projeto.id_status === 2) return false;
    return true;
  };

  const openFormModal = (projeto = null) => {
    // Se for edição (projeto não nulo), verificar permissão
    if (projeto && !canEditProject(projeto)) {
      if (userStored.diretor !== "S") {
        toast.warning("Apenas diretores podem editar projetos.");
      } else if (projeto.id_status === 2) {
        toast.warning("Projetos fechados não podem ser editados.");
      }
      return;
    }
    setEditingProjeto(projeto);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingProjeto(null);
  };

  const openDetalhes = (projeto) => {
    navigate(`/projectos/${projeto.id}`, { state: { projeto } });
  };

  const handleFormSuccess = () => {
    loadProjetos();
  };

  const countAberto = projetos.filter((p) => p.id_status === 1).length;
  const countFechado = projetos.filter((p) => p.id_status === 2).length;
  const countSuspenso = projetos.filter((p) => p.id_status === 3).length;
  const countCriticidadeAlta = projetos.filter((p) => p.id_criticidade === 3).length;

  const filtered = projetos.filter((p) => {
    const statusInfo = getStatusInfo(p.id_status);
    const matchFilter = filter === "TODOS" || statusInfo.label === filter;
    
    const criticidadeInfo = getCriticidadeInfo(p.id_criticidade);
    const matchCriticidade = criticidadeFilter === "TODAS" || criticidadeInfo.label === criticidadeFilter;
    
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      p.titulo?.toLowerCase().includes(q) ||
      p.descricao?.toLowerCase().includes(q);
    
    return matchFilter && matchCriticidade && matchSearch;
  });

  const columns = [
    {
      title: "Projeto",
      dataIndex: "titulo",
      key: "titulo",
      width: 280,
      render: (titulo, record) => {
        const iniciativa = iniciativaList.find(i => i.id === record.id_iniciativa);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Avatar
              shape="square"
              size="large"
              style={{
                backgroundColor: "#e6f4ff",
                color: "#1677ff",
                borderRadius: 8,
              }}
              icon={<FaProjectDiagram />}
            />
            <div>
              <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 4 }}>
                {titulo}
              </div>
              {iniciativa && (
                <Text type="secondary" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                  <FaLightbulb /> {iniciativa.titulo}
                </Text>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Criticidade",
      dataIndex: "id_criticidade",
      key: "criticidade",
      width: 120,
      render: (id_criticidade) => {
        const info = getCriticidadeInfo(id_criticidade);
        return (
          <Tag
            color={info.color}
            icon={info.icon}
            style={{
              borderRadius: 20,
              padding: "4px 12px",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {info.label}
          </Tag>
        );
      },
    },
    {
      title: "Período",
      key: "periodo",
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            {formatDate(record.inicio)}
          </Text>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            {formatDate(record.fim)}
          </Text>
        </Space>
      ),
    },
    {
      title: "Progresso",
      dataIndex: "progresso",
      key: "progresso",
      width: 180,
      render: (progresso) => {
        const valor = parseFloat(progresso) || 0;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Progress
              percent={valor}
              size="small"
              status={valor === 100 ? "success" : "active"}
              style={{ flex: 1 }}
              strokeColor={{
                "0%": "#1677ff",
                "100%": "#52c41a",
              }}
            />
            <Text strong style={{ minWidth: 45, textAlign: "right" }}>
              {valor}%
            </Text>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "id_status",
      key: "status",
      width: 130,
      render: (id_status) => {
        const { label, color, icon } = getStatusInfo(id_status);
        return (
          <Tag
            color={color}
            style={{
              borderRadius: 20,
              padding: "4px 12px",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {icon} {label}
          </Tag>
        );
      },
    },
    {
      title: "Aprovado",
      dataIndex: "aprovado",
      key: "aprovado",
      width: 100,
      render: (aprovado) =>
        aprovado === "S" ? (
          <Badge status="success" text="Sim" />
        ) : (
          <Badge status="error" text="Não" />
        ),
    },
    {
      title: "Responsáveis",
      key: "responsaveis",
      width: 180,
      render: (_, record) => {
        const nomes = record.responsaveisNomes?.map((r) => r.nome) || [];
        if (nomes.length === 0) return <Text type="secondary">—</Text>;
        const display = nomes.slice(0, 2).join(", ");
        const extra = nomes.length > 2 ? ` +${nomes.length - 2}` : "";
        return (
          <Tooltip title={nomes.join(", ")}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <FaUsers style={{ color: "#8c8c8c" }} />
              <Text ellipsis style={{ maxWidth: 120 }}>
                {display}
                {extra}
              </Text>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Ações",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          {/* Botão de editar só aparece se o usuário tiver permissão */}
          {canEditProject(record) && (
            <Tooltip title="Editar projeto">
              <Button
                type="text"
                icon={<FaEdit />}
                onClick={() => openFormModal(record)}
                style={{ color: "#1677ff" }}
              />
            </Tooltip>
          )}
          <Tooltip title="Ver detalhes">
            <Button
              type="text"
              icon={<FaEye />}
              onClick={() => openDetalhes(record)}
              style={{ color: "#52c41a" }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="projeto-layout" style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Cabeçalho da página */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(22, 119, 255, 0.15)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaProjectDiagram style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Gestão de Projetos
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}>
                    Planeje, acompanhe e entregue com excelência
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Button
                type="primary"
                size="large"
                icon={<FaPlus />}
                onClick={() => openFormModal()} // criação permitida a todos
                style={{
                  backgroundColor: "#fff",
                  color: "#1677ff",
                  border: "none",
                  fontWeight: 600,
                  borderRadius: 30,
                  padding: "0 24px",
                  height: 48,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                Novo Projeto
              </Button>
            </Col>
          </Row>
        </div>

        {/* Cards de estatísticas */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card
              className="stat-card"
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Total de Projetos</span>}
                value={projetos.length}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#1677ff" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#e6f4ff",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaProjectDiagram style={{ fontSize: 24, color: "#1677ff" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              className="stat-card"
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Projetos Abertos</span>}
                value={countAberto}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#1677ff" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#e6f4ff",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaPlayCircle style={{ fontSize: 24, color: "#1677ff" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              className="stat-card"
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Criticidade Alta</span>}
                value={countCriticidadeAlta}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#ff4d4f" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#fff1f0",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaExclamationTriangle style={{ fontSize: 24, color: "#ff4d4f" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              className="stat-card"
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Suspensos</span>}
                value={countSuspenso}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#faad14" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#fffbe6",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaPauseCircle style={{ fontSize: 24, color: "#faad14" }} />
                  </div>
                }
              />
            </Card>
          </Col>
        </Row>

        {/* Barra de ferramentas */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Input
                size="large"
                prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
                placeholder="Pesquisar por título ou descrição..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ borderRadius: 30 }}
              />
            </Col>
            <Col>
              <Select
                size="large"
                style={{ width: 160, borderRadius: 30 }}
                value={filter}
                onChange={setFilter}
                suffixIcon={<FaFilter style={{ color: "#1677ff" }} />}
              >
                <Option value="TODOS">Todos os status</Option>
                <Option value="ABERTO">Abertos</Option>
                <Option value="EM ANÁLISE">Em Análise</Option>
                <Option value="SUSPENSO">Suspensos</Option>
                <Option value="FECHADO">Fechados</Option>
              </Select>
            </Col>
            <Col>
              <Select
                size="large"
                style={{ width: 160, borderRadius: 30 }}
                value={criticidadeFilter}
                onChange={setCriticidadeFilter}
                suffixIcon={<FaFlag style={{ color: "#ff4d4f" }} />}
              >
                <Option value="TODAS">Todas criticidades</Option>
                <Option value="BAIXA">Baixa</Option>
                <Option value="MÉDIA">Média</Option>
                <Option value="ALTA">Alta</Option>
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Tabela de projetos */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} projetos`,
              style: { padding: "16px 24px" },
            }}
            locale={{ emptyText: "Nenhum projeto encontrado" }}
            scroll={{ x: 1200 }}
          />
        </Card>

        {/* Modal de formulário */}
        <ProjetoFormModal
          open={formModalOpen}
          onClose={closeFormModal}
          onSuccess={handleFormSuccess}
          editingProjeto={editingProjeto}
          userStored={userStored}
          criticidadeList={criticidadeList}
          iniciativaList={iniciativaList}
        />
      </Content>
    </Layout>
  );
};

export default Projeto;